import type { TaskGroup, TaskStatus } from './task-types';

export interface TaskCatalogEntry {
  id: string;
  group: TaskGroup;
  name: string;
  description: string;
  required: boolean;
  verdictOptions: TaskStatus[];
  requiresNote: boolean;
}

export const TASK_GROUP_LABELS: Record<TaskGroup, string> = {
  'tc-area': 'Type Curve Area',
  'dca-forecasts': 'DCA Forecasts',
  'tc-details': 'Type Curve Details',
  'verify-volumes': 'Verify Volumes',
  'ltd-inventory': 'LTD Inventory',
};

export const TASK_GROUP_ORDER: TaskGroup[] = [
  'tc-area',
  'dca-forecasts',
  'tc-details',
  'verify-volumes',
  'ltd-inventory',
];

export const TASK_CATALOG: TaskCatalogEntry[] = [
  // ── Type Curve Area ───────────────────────────────────────────────────────
  {
    id: 'verify-tc-area',
    group: 'tc-area',
    name: 'Verify Type Curve Area',
    description:
      'Confirm the selected type curve area is correct for this workspace.',
    required: true,
    verdictOptions: ['correct', 'incorrect'],
    requiresNote: false,
  },
  {
    id: 'resolve-unit-overlaps',
    group: 'tc-area',
    name: 'Resolve unit overlaps',
    description:
      'Some development units in this Type Curve Area overlap. Required before promoting, though the verdict itself does not wait for the overlaps: mark it correct or incorrect whenever you are ready. Generate LTD geometries is the step that stays locked until every overlap is resolved.',
    required: true,
    verdictOptions: ['correct', 'incorrect'],
    requiresNote: true,
  },

  // ── DCA Forecasts ─────────────────────────────────────────────────────────
  {
    id: 'verify-dca-scenario',
    group: 'dca-forecasts',
    name: 'Verify DCA Scenario',
    description:
      'Confirm the selected DCA scenario and its parameters are appropriate.',
    required: false,
    verdictOptions: ['correct', 'incorrect', 'na'],
    requiresNote: false,
  },

  // ── Type Curve Details ────────────────────────────────────────────────────
  {
    id: 'verify-tc-status',
    group: 'tc-details',
    name: 'Verify Status',
    description: 'Confirm the type curve status (Eval/Upside) is correct.',
    required: false,
    verdictOptions: ['correct', 'incorrect'],
    requiresNote: false,
  },
  {
    id: 'verify-tc-lateral-length',
    group: 'tc-details',
    name: 'Verify Lateral Length',
    description: 'Confirm the lateral length is correct.',
    required: false,
    verdictOptions: ['correct', 'incorrect'],
    requiresNote: false,
  },
  {
    id: 'verify-tc-formation',
    group: 'tc-details',
    name: 'Verify Formation',
    description: 'Confirm the formation is correct.',
    required: false,
    verdictOptions: ['correct', 'incorrect'],
    requiresNote: false,
  },
  {
    id: 'verify-tc-well-spacing',
    group: 'tc-details',
    name: 'Verify Well Spacing',
    description: 'Confirm the well spacing is correct.',
    required: false,
    verdictOptions: ['correct', 'incorrect'],
    requiresNote: false,
  },
  {
    id: 'verify-tc-proppant-intensity',
    group: 'tc-details',
    name: 'Verify Proppant Intensity',
    description: 'Confirm the proppant intensity is correct.',
    required: false,
    verdictOptions: ['correct', 'incorrect'],
    requiresNote: false,
  },
  {
    id: 'verify-tc-fluid-intensity',
    group: 'tc-details',
    name: 'Verify Fluid Intensity',
    description: 'Confirm the fluid intensity is correct.',
    required: false,
    verdictOptions: ['correct', 'incorrect'],
    requiresNote: false,
  },
  {
    id: 'verify-tc-vintage',
    group: 'tc-details',
    name: 'Verify Vintage',
    description: 'Confirm the vintage date is correct.',
    required: false,
    verdictOptions: ['correct', 'incorrect'],
    requiresNote: false,
  },
  {
    id: 'verify-tc-operator',
    group: 'tc-details',
    name: 'Verify Operator(s)',
    description: 'Confirm the operators selection is correct.',
    required: false,
    verdictOptions: ['correct', 'incorrect'],
    requiresNote: false,
  },
  {
    id: 'verify-tc-development-type',
    group: 'tc-details',
    name: 'Verify Development Type',
    description: 'Confirm the development type is correct.',
    required: false,
    verdictOptions: ['correct', 'incorrect'],
    requiresNote: false,
  },

  // ── Verify Volumes ────────────────────────────────────────────────────────
  {
    id: 'verify-oil-volumes',
    group: 'verify-volumes',
    name: 'Verify Oil Volumes',
    description: 'Confirm oil forecast volumes are reasonable.',
    required: false,
    verdictOptions: ['correct', 'incorrect', 'na'],
    requiresNote: false,
  },
  {
    id: 'verify-gas-volumes',
    group: 'verify-volumes',
    name: 'Verify Gas Volumes',
    description: 'Confirm gas forecast volumes are reasonable.',
    required: false,
    verdictOptions: ['correct', 'incorrect', 'na'],
    requiresNote: false,
  },
  {
    id: 'verify-water-volumes',
    group: 'verify-volumes',
    name: 'Verify Water Volumes',
    description: 'Confirm water forecast volumes are reasonable.',
    required: false,
    verdictOptions: ['correct', 'incorrect', 'na'],
    requiresNote: false,
  },

  // ── LTD Inventory ─────────────────────────────────────────────────────────
  {
    id: 'verify-inventory',
    group: 'ltd-inventory',
    name: 'Verify Inventory',
    description:
      'Verify the wells are landed correctly and LTD slots are generated correctly. Generate LTD Geometries and verify on a unit basis.',
    required: true,
    verdictOptions: ['correct', 'incorrect', 'na'],
    requiresNote: true,
  },
];

/** IDs of tasks backed by backend verification items. These POST to events endpoints. */
export const BACKEND_TASK_IDS = new Set([
  'resolve-unit-overlaps',
  'verify-inventory',
]);

/** Quick lookup by task ID. */
export const TASK_CATALOG_MAP = new Map(
  TASK_CATALOG.map((entry) => [entry.id, entry])
);
